import React, { useState, useContext, useEffect } from "react";
import { PrayerPartnerContext } from "../../contexts/PrayerPartnerContext";
import styled from "styled-components";
import { callTypes } from "../../assets/data.js";

export const TestimonyDialog = ({ isOpen, onClose, callDetail, onSuccess, isReviewMode = false }) => {
    const { createTestimony } = useContext(PrayerPartnerContext);
    
    // Debug: Log the callDetail to see available fields
    console.log('CallDetail received:', callDetail);
    
    const [formData, setFormData] = useState({
        testimony: '',
        name: callDetail?.caller_first_name || '',
        last_name: callDetail?.caller_last_name || '',
        category: '',
        task_sid: callDetail?.task_sid || '',
        twilio_number: callDetail?.twilio_number || callDetail?.twilio_phone || callDetail?.phone_number || ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Update formData when callDetail changes - but only set twilio_number initially
    useEffect(() => {
        if (callDetail) {
            setFormData(prev => ({
                ...prev,
                name: callDetail.caller_first_name || '',
                last_name: callDetail.caller_last_name || '',
                task_sid: callDetail.task_sid || '',
                // Only set twilio_number if it's not already set (initial load)
                twilio_number: prev.twilio_number || callDetail.twilio_number || callDetail.twilio_phone || callDetail.phone_number || ''
            }));
        }
    }, [callDetail]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        setIsSubmitting(true);
        
        // Debug: Log the data being sent
        console.log('Submitting testimony data:', formData);
        
        try {
            await createTestimony(formData);
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error creating testimony:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <DialogOverlay>
            <DialogContent>
                <DialogHeader>
                    <h2>{isReviewMode ? 'Review Testimony' : 'Create New Testimony'}</h2>
                    <CloseButton onClick={onClose}>&times;</CloseButton>
                </DialogHeader>
                
                <form onSubmit={handleSubmit}>
                    {/* Display Call Notes */}
                    {callDetail?.notes && (
                        <CallNotesSection>
                            <h3>Call Notes:</h3>
                            <p>{callDetail.notes}</p>
                        </CallNotesSection>
                    )}



                    <FormGroup>
                        <label htmlFor="name">First Name:</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                        />
                    </FormGroup>

                    <FormGroup>
                        <label htmlFor="last_name">Last Name:</label>
                        <input
                            type="text"
                            id="last_name"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleInputChange}
                        />
                    </FormGroup>

                    <FormGroup>
                        <label htmlFor="twilio_number">Twilio Number: *</label>
                        <input
                            type="text"
                            id="twilio_number"
                            name="twilio_number"
                            value={formData.twilio_number}
                            onChange={handleInputChange}
                            placeholder="Enter the Twilio phone number"
                            readOnly={isReviewMode}
                            disabled={isReviewMode}
                            className={isReviewMode ? 'readonly' : ''}
                            required
                        />
                        <small style={{color: '#6b7280', fontSize: '0.875rem'}}>
                            This is the phone number used to make the call
                        </small>
                    </FormGroup>

                    <FormGroup>
                        <label htmlFor="category">Category:</label>
                        <select
                            id="category"
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Select a category</option>
                            {callTypes.map(category => (
                                <option key={category.id || category.key} value={category.type || category.key}>
                                    {category.type || category.key}
                                </option>
                            ))}
                        </select>
                    </FormGroup>

                    <FormGroup>
                        <label htmlFor="testimony">Testimony:</label>
                        <textarea
                            id="testimony"
                            name="testimony"
                            value={formData.testimony}
                            onChange={handleInputChange}
                            rows={6}
                            placeholder="Share your testimony here..."
                            required
                        />
                    </FormGroup>

                    <DialogActions>
                        <CancelButton type="button" onClick={onClose}>
                            Cancel
                        </CancelButton>
                        <SubmitButton type="submit" disabled={isSubmitting}>
                            {isReviewMode ? 'Create Testimony' : (isSubmitting ? 'Creating...' : 'Create Testimony')}
                        </SubmitButton>
                    </DialogActions>
                </form>
            </DialogContent>
        </DialogOverlay>
    );
};

const DialogOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const DialogContent = styled.div`
    background: white;
    border-radius: 8px;
    padding: 0;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const DialogHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 20px 0 20px;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 20px;

    h2 {
        margin: 0;
        color: #1f2937;
        font-size: 1.5rem;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #6b7280;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &:hover {
        background-color: #f3f4f6;
        color: #374151;
    }
`;

const CallNotesSection = styled.div`
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 15px;
    margin: 0 20px 20px 20px;

    h3 {
        margin: 0 0 10px 0;
        color: #374151;
        font-size: 1rem;
        font-weight: 600;
    }

    p {
        margin: 0;
        color: #6b7280;
        line-height: 1.5;
    }
`;



const FormGroup = styled.div`
    margin-bottom: 20px;
    padding: 0 20px;

    label {
        display: block;
        margin-bottom: 5px;
        color: #374151;
        font-weight: 500;
    }

    input, select, textarea {
        width: 100%;
        padding: 10px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 1rem;
        transition: border-color 0.2s;

        &:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        &.readonly {
            background-color: #f9fafb;
            color: #6b7280;
            cursor: not-allowed;
            border-color: #e5e7eb;
        }

        &:disabled {
            background-color: #f9fafb;
            color: #6b7280;
            cursor: not-allowed;
            border-color: #e5e7eb;
        }
    }

    textarea {
        resize: vertical;
        min-height: 120px;
    }

    small {
        display: block;
        margin-top: 5px;
    }
`;

const DialogActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px;
    border-top: 1px solid #e5e7eb;
    margin-top: 20px;
`;

const Button = styled.button`
    padding: 10px 20px;
    border-radius: 6px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const CancelButton = styled(Button)`
    background-color: #f3f4f6;
    color: #374151;

    &:hover:not(:disabled) {
        background-color: #e5e7eb;
    }
`;

const SubmitButton = styled(Button)`
    background-color: #3b82f6;
    color: white;

    &:hover:not(:disabled) {
        background-color: #2563eb;
    }
`;
